import { SCHOOLS_WITH_COORDINATES } from "@/constant/variables";
import { findPlace, type NearPlace } from "./nearPlaces";
import { propertyCoords, type PropertyLite } from "./property";

export { describeNear, findPlace, findPlaceByText, matchPlaces, nearKey, nearLabel, parseNear } from "./nearPlaces";
export type { NearKind, NearPlace } from "./nearPlaces";

/** "Near" a school means within this many metres when both sides have coordinates. */
export const NEAR_RADIUS_M = 2000;

function normName(s: string): string {
  return s.toUpperCase().replace(/[^A-Z0-9]+/g, " ").trim();
}

function stripLevel(s: string): string {
  return s.replace(/\b(PRIMARY|SCHOOL)\b/g, "").replace(/\s+/g, " ").trim();
}

type SchoolCoord = { school_name: string; lat: number; lng: number };

function schoolCoords(label: string): { lat: number; lng: number } | null {
  const list = SCHOOLS_WITH_COORDINATES as ReadonlyArray<Partial<SchoolCoord>>;
  const target = normName(label);
  const loose = stripLevel(target);
  const exact = list.find((c) => c.school_name && normName(c.school_name) === target);
  const hit =
    exact ??
    list.find((c) => c.school_name && !/SECONDARY/.test(c.school_name) && stripLevel(normName(c.school_name)) === loose);
  if (!hit || typeof hit.lat !== "number" || typeof hit.lng !== "number") return null;
  return { lat: hit.lat, lng: hit.lng };
}

/**
 * Resolve `mrt:<slug>` or `school:<slug>` to a place. Schools pick up
 * coordinates from the MOE list when the name matches; stations carry
 * districts only.
 */
export function resolveNear(near: string): NearPlace | null {
  const place = findPlace(near);
  if (!place) return null;
  if (place.kind === "school") {
    const c = schoolCoords(place.label);
    if (c) return { ...place, lat: c.lat, lng: c.lng };
  }
  return place;
}

export function haversineM(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const r = 6371000;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(h));
}

/** Metres from the project to the place, or null when either side lacks coordinates. */
export function distanceToPlace(p: PropertyLite, place: NearPlace): number | null {
  if (place.lat == null || place.lng == null) return null;
  const c = propertyCoords(p);
  if (!c) return null;
  return haversineM(c.lat, c.lng, place.lat, place.lng);
}

/** Within the radius when coordinates exist on both sides, else a district match. */
export function isNearPlace(p: PropertyLite, place: NearPlace): boolean {
  const d = distanceToPlace(p, place);
  if (d != null) return d <= NEAR_RADIUS_M;
  return place.districts.includes((p.district ?? "").toUpperCase());
}
